import { useState, useRef } from "react";

interface Accommodation {
  id: number;
  name: string;
  area: string;
  tag: string;
  hashtags: string[];
  rating: number;
  reviewCount: number;
  price: string;
  maxGuests: number;
  beds: number;
  image: string;
  detailImages?: string[];
}

interface Props {
  accommodation: Accommodation;
}

export default function DetailHero({ accommodation }: Props) {
  const [activeImg, setActiveImg] = useState(0);
  const allImages = [accommodation.image, ...(accommodation.detailImages || [])];
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);

  const handlePrev = () => setActiveImg((p) => (p - 1 + allImages.length) % allImages.length);
  const handleNext = () => setActiveImg((p) => (p + 1) % allImages.length);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
      if (dx < 0) handleNext();
      else handlePrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  };

  return (
    <section className="w-full bg-stone-50">
      {/* Main image */}
      <div
        className="relative w-full h-[260px] sm:h-[380px] md:h-[480px] lg:h-[580px] overflow-hidden bg-stone-200 select-none"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={allImages[activeImg]}
          alt={accommodation.name}
          className="w-full h-full object-cover object-top transition-all duration-500"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

        {/* Area / Tag badges */}
        <div className="absolute top-3 left-3 sm:top-6 sm:left-6 flex gap-2 flex-wrap">
          <span className="bg-amber-500/90 backdrop-blur-sm text-white text-xs sm:text-sm font-semibold px-3 py-1 sm:px-4 sm:py-1.5 rounded-full">
            {accommodation.area}
          </span>
          <span className="bg-white/90 backdrop-blur-sm text-stone-700 text-xs sm:text-sm font-medium px-3 py-1 sm:px-4 sm:py-1.5 rounded-full">
            {accommodation.tag}
          </span>
        </div>

        {/* Image counter */}
        <div className="absolute bottom-3 right-3 sm:bottom-6 sm:right-6 bg-black/50 backdrop-blur-sm text-white text-xs px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-full">
          {activeImg + 1} / {allImages.length}
        </div>

        {/* Arrow buttons */}
        {allImages.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-stone-700 text-base sm:text-lg"></i>
            </button>
            <button
              onClick={handleNext}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/80 hover:bg-white rounded-full transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-stone-700 text-base sm:text-lg"></i>
            </button>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {allImages.length > 1 && (
        <div className="flex gap-2 px-4 md:px-0 md:max-w-4xl md:mx-auto py-2.5 sm:py-3 overflow-x-auto">
          {allImages.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveImg(i)}
              className={`flex-shrink-0 w-16 h-11 sm:w-20 sm:h-14 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                activeImg === i ? "border-amber-500 scale-105" : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img src={img} alt={`photo ${i + 1}`} className="w-full h-full object-cover object-top" />
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
